import { MAX_LEVEL } from '../../content/balance';
import type { AccountSave } from '../../game/account';
import type { CharacterSave, DailyState } from '../../game/character';
import { featureSave } from '../economy/saveExt';

/**
 * The day cycle (research 02 §11.6). Two clock modes:
 * - **Session days** (default): the day advances when the player Rests at the
 *   Bounty Board, an inn or Haven Isle once the daily core is done (or any
 *   time with "Rest anytime"). Seven days make a week.
 * - **Real time**: daily reset at 06:00 local, weekly every 7 days (Wednesday).
 *
 * Per-character daily/weekly state (`char.daily`, `char.weekly`) rolls over
 * lazily when the account clock has moved on. Unused Rift Energy and
 * Colossus claims turn into Rested points (+10 per unused run/claim, cap
 * 100); a rested run spends 20 to double its rewards.
 */

export const RIFT_ENERGY_PER_DAY = 100;
export const RIFT_RUN_COST = 50;
export const REST_PER_UNUSED = 10;
export const REST_CAP = 100;
export const REST_PER_RUN = 20;
export const DAYS_PER_WEEK = 7;
export const RESET_HOUR = 6;
const DAY_MS = 86_400_000;

/** Rift Delve / Colossus Hunt open at the level cap. */
export const endgameUnlocked = (c: CharacterSave): boolean => c.level >= MAX_LEVEL;

/** Rested points earned for `elapsed` day ends, the first being the day recorded in `d`. */
export function restEarned(d: DailyState, elapsed: number): { rift: number; colossus: number } {
  if (elapsed <= 0) return { rift: 0, colossus: 0 };
  const runsPerDay = RIFT_ENERGY_PER_DAY / RIFT_RUN_COST;
  const unusedRuns = Math.floor(Math.max(0, d.riftEnergy) / RIFT_RUN_COST);
  return {
    rift: (unusedRuns + (elapsed - 1) * runsPerDay) * REST_PER_UNUSED,
    colossus: ((d.colossusClaimed ? 0 : 1) + (elapsed - 1)) * REST_PER_UNUSED,
  };
}

export interface DayRollover {
  /** Days since the character's recorded day (0 for a first sync). */
  elapsed: number;
  restRift: number;
  restColossus: number;
  /** Bounty offers of the finished day. */
  oldBounties: string[];
}

/** Rolls a character's daily state to the account day. Returns null when already current. */
export function rollDaily(c: CharacterSave, a: AccountSave): DayRollover | null {
  const d = c.daily;
  if (d.day === a.day) return null;
  const elapsed = d.day > 0 && a.day > d.day ? a.day - d.day : 0;
  const earned = endgameUnlocked(c) ? restEarned(d, elapsed) : { rift: 0, colossus: 0 };
  const riftRested = Math.min(REST_CAP, d.riftRested + earned.rift);
  const colossusRested = Math.min(REST_CAP, d.colossusRested + earned.colossus);
  const out: DayRollover = {
    elapsed,
    restRift: riftRested - d.riftRested,
    restColossus: colossusRested - d.colossusRested,
    oldBounties: [...d.bounties],
  };
  c.daily = {
    ...d,
    day: a.day,
    riftEnergy: RIFT_ENERGY_PER_DAY,
    riftRested,
    colossusClaimed: false,
    colossusRested,
    bounties: [],
    bountiesDone: [],
    fieldBoss: false,
  };
  return out;
}

/** Rolls weekly lockouts and bounties to the account week. Returns the old offers, or null. */
export function rollWeekly(c: CharacterSave, a: AccountSave): { oldBounties: string[] } | null {
  const w = c.weekly;
  if (w.week === a.week) return null;
  const out = { oldBounties: [...w.bounties] };
  c.weekly = {
    ...w,
    week: a.week,
    deepvault: [],
    siege: [],
    siegeMode: null,
    bounties: [],
    bountiesDone: [],
  };
  return out;
}

/** Spends 20 Rested for a double-reward run (for the Rift / Colossus features). */
export function consumeRested(c: CharacterSave, kind: 'rift' | 'colossus'): boolean {
  const d = c.daily;
  const cur = kind === 'rift' ? d.riftRested : d.colossusRested;
  if (cur < REST_PER_RUN) return false;
  if (kind === 'rift') d.riftRested -= REST_PER_RUN;
  else d.colossusRested -= REST_PER_RUN;
  return true;
}

// ---------------------------------------------------------------- daily core & resting

export interface CoreTask {
  id: 'bounties' | 'rift' | 'colossus';
  label: string;
  detail: string;
  done: boolean;
}

/**
 * What must be done before Resting in session-day mode: the Rift Delve
 * energy and the Colossus claim at the level cap, otherwise today's three
 * daily bounties.
 */
export function dailyCore(c: CharacterSave): CoreTask[] {
  const d = c.daily;
  if (endgameUnlocked(c)) {
    return [
      {
        id: 'rift',
        label: 'Rift Delve',
        detail: `${Math.max(0, d.riftEnergy)} Rift Energy left`,
        done: d.riftEnergy < RIFT_RUN_COST,
      },
      {
        id: 'colossus',
        label: 'Colossus Hunt claim',
        detail: d.colossusClaimed ? 'Claimed' : 'Not claimed',
        done: d.colossusClaimed,
      },
    ];
  }
  const need = Math.max(1, Math.min(3, d.bounties.length || 3));
  const have = d.bountiesDone.length;
  return [
    {
      id: 'bounties',
      label: 'Daily bounties',
      detail: `${Math.min(have, need)}/${need} claimed`,
      done: have >= need,
    },
  ];
}

export interface RestContext {
  sessionDays: boolean;
  relaxed: boolean;
  /** At the Bounty Board, an inn (town) or Haven Isle. */
  atRestSpot: boolean;
}

export function restStatus(c: CharacterSave, ctx: RestContext): { ok: boolean; reason: string } {
  if (!ctx.sessionDays)
    return { ok: false, reason: 'Days follow real time (daily reset at 06:00).' };
  if (!ctx.atRestSpot)
    return { ok: false, reason: 'Rest at the Bounty Board, in town or on Haven Isle.' };
  const open = dailyCore(c).filter((t) => !t.done);
  if (!ctx.relaxed && open.length)
    return {
      ok: false,
      reason: `Finish today's core first: ${open.map((t) => t.label).join(', ')}.`,
    };
  return { ok: true, reason: '' };
}

/** Session mode: advances the account clock by `days`; returns true if a new week began. */
export function advanceDay(a: AccountSave, days = 1): boolean {
  let newWeek = false;
  for (let i = 0; i < days; i++) {
    a.day++;
    if ((a.day - 1) % DAYS_PER_WEEK === 0) {
      a.week++;
      newWeek = true;
    }
  }
  return newWeek;
}

// ---------------------------------------------------------------- real-time mode

/** Days since the epoch, where each day starts at 06:00 local time. */
export function realDayIndex(now: number): number {
  const local = now - new Date(now).getTimezoneOffset() * 60_000;
  return Math.floor((local - RESET_HOUR * 3_600_000) / DAY_MS);
}

/** Weeks start on Wednesday (epoch day 6 was 1970-01-07, a Wednesday). */
export const realWeekIndex = (dayIndex: number): number =>
  Math.floor((dayIndex - 6) / DAYS_PER_WEEK);

/** Timestamp of the next 06:00 local reset. */
export function nextRealReset(now: number): number {
  const d = new Date(now);
  const reset = new Date(d.getFullYear(), d.getMonth(), d.getDate(), RESET_HOUR, 0, 0, 0);
  if (reset.getTime() <= now) reset.setDate(reset.getDate() + 1);
  return reset.getTime();
}

/**
 * Real-time mode: advances the account day/week when 06:00 boundaries have
 * passed since the last check. The first call only anchors the clock.
 * Returns the number of days advanced.
 */
export function tickRealClock(a: AccountSave, now: number): number {
  const fs = featureSave(a);
  const day = realDayIndex(now);
  const week = realWeekIndex(day);
  if (!fs.clock || fs.clock.mode !== 'real') {
    fs.clock = { mode: 'real', realDay: day, realWeek: week };
    return 0;
  }
  const dd = day - fs.clock.realDay;
  if (dd <= 0) return 0;
  a.day += dd;
  a.week += Math.max(0, week - fs.clock.realWeek);
  fs.clock.realDay = day;
  fs.clock.realWeek = week;
  return dd;
}

/** Session mode marks the clock so switching to real time re-anchors instead of jumping. */
export function markSessionClock(a: AccountSave): void {
  const fs = featureSave(a);
  if (fs.clock?.mode !== 'session') fs.clock = { mode: 'session', realDay: 0, realWeek: 0 };
}
